import { Injectable } from "@angular/core";
import { Constants } from "@ramudden/core/constants";
import { MeasuringPointUtils } from "@ramudden/core/utils";
import { IProject } from "@ramudden/models/project";
import { ServiceRequestOptions } from "@ramudden/models/search";
import { IMeasuringPointSummary } from "@ramudden/models/web";
import { SessionStorageService } from "./storage.service";

export class ValidationContext {
    allMeasuringPoints: IMeasuringPointSummary[];

    constructor(
        readonly from: Date,
        readonly to: Date,
        readonly measuringPoints: IMeasuringPointSummary[],
        readonly projects: IProject[],
    ) {
        if (!this.measuringPoints) this.measuringPoints = [];
        if (!this.projects) this.projects = [];

        for (const project of this.projects) {
            if (project.projectMeasuringPoints && project.projectMeasuringPoints.length > 0) {
                this.allMeasuringPoints = this.allMeasuringPoints.concat(
                    project.projectMeasuringPoints
                        .sort((gmp1, gmp2) => (gmp1.sortOrder > gmp2.sortOrder ? 1 : -1))
                        .map((x) => MeasuringPointUtils.toSummary(x.measuringPoint)),
                );
            }
        }
    }
}

class StoredValidationContext {
    from: Date;
    to: Date;
    measuringPoints: IMeasuringPointSummary[];
    projects: IProject[];
}

@Injectable({
    providedIn: "root",
})
export class ValidationService {
    private context: ValidationContext;
    private fetchedStoredContext = false;

    constructor(private readonly sessionStorage: SessionStorageService) {}

    setContext(context: ValidationContext) {
        if (context) {
            const normalizeTime = (date: Date) => {
                if (!date) return;
                date.setHours(0);
                date.setMinutes(0);
                date.setSeconds(0);
                date.setMilliseconds(0);
            };

            normalizeTime(context.from);
            normalizeTime(context.to);
        }

        this.context = context;
        this.storeContext();
    }

    async getContext(): Promise<ValidationContext> {
        if (!this.fetchedStoredContext) {
            this.fetchedStoredContext = true;

            const fetchedContext = await this.getStoredContext();
            this.context = this.context || fetchedContext;
        }

        return this.context;
    }

    getHours(): Date[] {
        const dates = new Array<Date>();

        for (let date = new Date(this.context.from); date < this.context.to.addDays(1); date = date.addHours(1)) {
            dates.push(new Date(date));
        }

        return dates;
    }

    getDates(): Date[] {
        const dates = new Array<Date>();

        for (let date = new Date(this.context.from); date < this.context.to.addDays(1); date = date.addDays(1)) {
            dates.push(new Date(date));
        }

        return dates;
    }

    getMeasuringPoint(id: number) {
        if (!this.context) return null;

        return this.context.allMeasuringPoints.find((x) => x.id === id);
    }

    //#region Storage

    private storeContext() {
        if (this.context) {
            // Convert to tiny, json friendly format

            const toStore = new StoredValidationContext();
            toStore.from = this.context.from;
            toStore.to = this.context.to;
            toStore.measuringPoints = this.context.measuringPoints.clone();
            toStore.projects = this.context.projects.clone();

            this.sessionStorage.setItem(Constants.validationStorageKey, JSON.stringify(toStore));
        } else {
            this.sessionStorage.removeItem(Constants.validationStorageKey);
        }
    }

    private async getStoredContext(): Promise<ValidationContext> {
        const stored = this.sessionStorage.getItem(Constants.validationStorageKey);
        if (!stored) return null;

        const parsed = JSON.parse(stored) as StoredValidationContext;

        const serviceRequestOptions = new ServiceRequestOptions();
        serviceRequestOptions.includes.add("group", "measuringPoints");

        const measuringPoints = parsed.measuringPoints;
        const projects = parsed.projects;

        return new ValidationContext(new Date(parsed.from), new Date(parsed.to), measuringPoints, projects);
    }

    //#endregion Storage
}
